<div class="justup-dialog ca-par-periode-dialog">
  <vl-dialog-header-export
    [exporting]="exporting()"
    (close)="dialogRef.close()"
    (export)="exportExcel()">
    CA par {{ scaleLabelSingularLower[scale()] }}
  </vl-dialog-header-export>

  <div class="justup-dialog-content flex-col gap-16">

    <!-- ═══ Granularité au-dessus, centrée — comme dans repartition-ca-dialog ═══ -->
    <div class="flex-row justify-center">
      <vl-chips-toggle [elements]="scaleElements" [(value)]="scale" size="small"/>
    </div>

    <!-- ═══ Stats cards colorées via vl-radio-card ═══ -->
    <div class="flex-row gap-8 wrap">
      <vl-radio-card mode="display" color="valid" icon="euro"
                     title="Valeur totale"
                     style="flex: 1; min-width: 160px">
        <span content class="headline">{{ stats().totalFormatted }}</span>
      </vl-radio-card>

      <vl-radio-card mode="display" color="valid" icon="trending_up"
                     title="Moyenne / {{ scaleLabelSingularLower[scale()] }}"
                     style="flex: 1; min-width: 160px">
        <span content class="headline">{{ stats().avgFormatted }}</span>
      </vl-radio-card>

      <vl-radio-card mode="display" color="secondary" icon="emoji_events"
                     [title]="scaleBestLabel[scale()]"
                     style="flex: 1; min-width: 160px">
        <div content class="flex-col gap-2">
          <span class="body-1" style="font-weight: 700; overflow: hidden; text-overflow: ellipsis; white-space: nowrap">
            {{ stats().bestLabel }}
          </span>
          <span class="tiny" style="opacity: 0.7">{{ stats().bestCAFormatted }}</span>
        </div>
      </vl-radio-card>

      <vl-radio-card mode="display" color="warn" icon="leaderboard"
                     title="Concentration 80%"
                     style="flex: 1; min-width: 160px">
        <div content class="flex-col gap-2">
          <div class="flex-row align-baseline gap-4">
            <span class="headline">{{ stats().pareto80 }}</span>
            <span class="body-2" style="opacity: 0.7">/ {{ stats().count }}</span>
          </div>
          <span class="tiny" style="opacity: 0.7">
            {{ scaleLabelPlural[scale()] }} pour 80% du CA
          </span>
        </div>
      </vl-radio-card>
    </div>

    <!-- ═══ Header columns : mêmes codes couleurs que la table (gris CA, accent PART) ═══ -->
    <div class="flex-row gap-8 p-8 align-center"
         style="border-bottom: 1px solid var(--color-on-disabled)">
      <span class="shrink-0" style="width: 16px"></span>
      <span class="shrink-0" style="width: 10px"></span>
      <span class="grow tiny on-background-alt col-header"
            (click)="toggleSort('date')"
            style="cursor: pointer">
        {{ scaleLabel[scale()] | titlecase }}
        @if (sortKey() === 'date') {
          <mat-icon class="sort-icon">{{ sortDesc() ? 'arrow_downward' : 'arrow_upward' }}</mat-icon>
        }
      </span>
      <span class="tiny on-background-alt shrink-0 col-header col-num-header"
            (click)="toggleSort('ca')"
            matTooltip="Chiffre d'affaires HT de la période"
            style="cursor: pointer">
        CA
        @if (sortKey() === 'ca') {
          <mat-icon class="sort-icon">{{ sortDesc() ? 'arrow_downward' : 'arrow_upward' }}</mat-icon>
        }
      </span>
      <span class="tiny on-background-alt shrink-0 col-header col-part-header"
            matTooltip="Part du CA total sur la période sélectionnée">
        Part
      </span>
      @if (hasFactureData()) {
        <span class="tiny on-background-alt shrink-0 col-header col-num-header"
              (click)="toggleSort('nbFactures')"
              matTooltip="Nombre de factures émises sur la période"
              style="cursor: pointer">
          Factures
          @if (sortKey() === 'nbFactures') {
            <mat-icon class="sort-icon">{{ sortDesc() ? 'arrow_downward' : 'arrow_upward' }}</mat-icon>
          }
        </span>
      }
    </div>

    <!-- ═══ Rows : chaque période en carte avec border-left colorée + dot ═══ -->
    <div class="flex-col gap-2">
      @for (row of rows(); track row.key) {
        <div class="period-row flex-col">
          <div class="flex-row gap-8 p-8 align-center"
               [style.border-left]="'3px solid ' + row.color"
               style="border: 1px solid var(--color-outline); border-radius: 8px; cursor: pointer"
               (click)="togglePeriod(row.key)">
            <mat-icon class="on-disabled shrink-0"
                      style="font-size: 16px; width: 16px; height: 16px">
              {{ isExpanded(row.key) ? 'expand_more' : 'chevron_right' }}
            </mat-icon>
            <span class="shrink-0 border-full"
                  [style.background-color]="row.color"
                  style="width: 10px; height: 10px"></span>
            <span class="grow caption on-background-alt period-label">
              {{ row.label }}
            </span>
            <span class="tiny on-disabled shrink-0 col-num">
              {{ formatEuroExact(row.ca) }}
            </span>
            <span class="tiny on-accent shrink-0 col-part">
              {{ row.partPct | number:'1.1-1':'fr-FR' }}%
            </span>
            @if (hasFactureData()) {
              <span class="tiny on-disabled shrink-0 col-num">
                {{ row.nbFactures }}
              </span>
            }
          </div>

          <!-- Détail expandé : tous les clients de la période -->
          @if (isExpanded(row.key)) {
            <div class="flex-col gap-2 mt-2 mb-2" style="margin-left: 28px">
              <span class="tiny on-background-alt" style="font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em">
                Clients ({{ row.nbClients }})
              </span>
              @for (c of row.clients; track c.denomination) {
                <div class="flex-col">
                  <div class="flex-row gap-8 align-center p-4 client-row"
                       style="cursor: pointer"
                       (click)="toggleClient(row.key, c.denomination); $event.stopPropagation()">
                    <mat-icon class="on-disabled shrink-0"
                              style="font-size: 14px; width: 14px; height: 14px">
                      {{ isClientExpanded(row.key, c.denomination) ? 'expand_more' : 'chevron_right' }}
                    </mat-icon>
                    <span class="shrink-0 client-dot" [style.background-color]="row.color"></span>
                    <span class="grow caption on-background-alt client-name">
                      {{ c.denomination }}
                    </span>
                    <span class="tiny on-disabled shrink-0" style="min-width: 24px; text-align: right">
                      {{ c.nbEntries }}
                    </span>
                    <span class="tiny on-disabled shrink-0 col-num">
                      {{ formatEuroExact(c.ca) }}
                    </span>
                    <span class="tiny on-accent shrink-0 col-part">
                      {{ c.partPeriod | number:'1.1-1':'fr-FR' }}%
                    </span>
                  </div>

                  <!-- Détail factures/entrées du client -->
                  @if (isClientExpanded(row.key, c.denomination)) {
                    <div class="flex-col gap-1" style="margin-left: 36px; margin-bottom: 4px">
                      @for (e of c.entries; track $index) {
                        <div class="flex-row gap-8 align-center p-2 entry-row">
                          <span class="tiny on-disabled shrink-0" style="min-width: 90px">
                            {{ e.dateLabel }}
                          </span>
                          @if (e.factureIds.length) {
                            <span class="tiny on-background-alt grow entry-ids">
                              <mat-icon style="font-size: 12px; width: 12px; height: 12px; vertical-align: middle; margin-right: 2px">receipt_long</mat-icon>
                              {{ e.factureIds.join(', ') }}
                            </span>
                          }
                          @if (e.commandeIds.length) {
                            <span class="tiny on-background-alt shrink-0 entry-ids">
                              <mat-icon style="font-size: 12px; width: 12px; height: 12px; vertical-align: middle; margin-right: 2px">shopping_cart</mat-icon>
                              {{ e.commandeIds.join(', ') }}
                            </span>
                          }
                          @if (!e.factureIds.length && !e.commandeIds.length) {
                            <span class="grow"></span>
                          }
                          <span class="tiny on-disabled shrink-0 col-num">
                            {{ formatEuroExact(e.montant) }}
                          </span>
                        </div>
                      }
                    </div>
                  }
                </div>
              }
            </div>
          }
        </div>
      } @empty {
        <div class="flex-row justify-center p-32">
          <span class="caption on-background-alt">Aucune donnée sur la période</span>
        </div>
      }
    </div>
  </div>
</div>
